/* AppsList — entry screen of App Studio: every BeX App in the org as a
 * card, plus Create App (bootstrap). Lives outside the per-app shell. */
import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    useNavigate,
    useParams
} from 'react-router-dom';
import {
    ArrowRight,
    BookOpen,
    Boxes,
    Copy,
    Loader2,
    Pencil,
    Plus,
    RefreshCw,
    Trash2,
} from 'lucide-react';
import {
    appEngineService
} from '../api/appEngine.service';
import CreateAppDialog from './CreateAppDialog';
import ConfirmDialog from '../shared/ConfirmDialog';
import NamePromptDialog from '../shared/NamePromptDialog';
import {
    notify
} from 'shared/api/notifier';
import {
    readStoredMode
} from '../ui/themeMode';
import type {
    AppListEntry
} from '../api/types';
import '../ui/tokens.css';
import '../ui/shell.css';

/** Default name for a duplicate: "<base> - duplicate N" where N is the next
 * free positive integer among existing "<base> - duplicate K" names in the
 * org — mirroring BeXO's own auto-increment so the pre-filled name matches
 * what the engine would pick when none is supplied. */
function nextDuplicateName(baseName: string, apps: AppListEntry[]): string {
    const escaped = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^${escaped} - duplicate (\\d+)$`);
    const used = new Set<number>();
    apps.forEach((a) => {
        const m = (a.name || '').trim().match(re);
        if (m) used.add(parseInt(m[1], 10));
    });
    let n = 1;
    while (used.has(n)) n += 1;
    return `${baseName} - duplicate ${n}`;
}

export default function AppsList() {
    const nav = useNavigate();
    const params = useParams<any>();
    const orgId = params.oragID || params.orgId || '';
    const [apps, setApps] = useState<AppListEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [listUnavailable, setListUnavailable] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [manualId, setManualId] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<AppListEntry | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [duplicateTarget, setDuplicateTarget] = useState<AppListEntry | null>(null);
    const [renameTarget, setRenameTarget] = useState<AppListEntry | null>(null);
    // Prominent in-flight overlay for duplicate / rename (can't be missed).
    const [busyAction, setBusyAction] =
        useState<{ kind: 'duplicate' | 'rename'; name: string } | null>(null);
    const mode = readStoredMode();

    // Mirror theme + mode on <body> so this page (outside the shell) and its
    // portaled dialogs (CreateAppDialog) still read appstudio tokens.
    useEffect(() => {
        document.body.setAttribute('data-theme', 'appstudio');
        document.body.setAttribute('data-aps-mode', mode);
        return () => {
            document.body.removeAttribute('data-theme');
            document.body.removeAttribute('data-aps-mode');
        };
    }, [mode]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await appEngineService.listApps(orgId);
            setApps(list);
            setListUnavailable(false);
        } catch {
            // GET /apps?org_id= is a UI-driven contract addition — degrade
            // gracefully until the backend ships it.
            setApps([]);
            setListUnavailable(true);
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        load();
    }, [load]);

    const openApp = (appId: string) => {
        nav(`/organizations/${orgId}/3dapp-studio/app/${appId}/overview`);
    };

    const doDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await appEngineService.deleteApp(orgId, deleteTarget.id);
            notify(`Deleted "${deleteTarget.name || deleteTarget.id}"`, 'success');
            setDeleteTarget(null);
            load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || e?.message || 'Delete failed', 'danger');
        } finally {
            setDeleting(false);
        }
    };

    const doDuplicate = async (a: AppListEntry, chosenName: string) => {
        const name = chosenName.trim() || (a.name || a.id);
        setDuplicateTarget(null);
        setBusyAction({ kind: 'duplicate', name });
        try {
            await appEngineService.duplicateApp(orgId, a.id, { name });
            notify(`Created duplicate "${name}"`, 'success');
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || e?.message || 'Duplicate failed', 'danger');
        } finally {
            setBusyAction(null);
        }
    };

    const doRename = async (a: AppListEntry, chosenName: string) => {
        const name = chosenName.trim();
        if (!name) return;
        setRenameTarget(null);
        setBusyAction({ kind: 'rename', name });
        try {
            await appEngineService.renameApp(orgId, a.id, name);
            notify(`Renamed to "${name}"`, 'success');
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || e?.message || 'Rename failed', 'danger');
        } finally {
            setBusyAction(null);
        }
    };

    return (
        <div className="aps-root aps-root-embedded" data-theme="appstudio" data-aps-mode={mode}>
            <div className="aps-page">
                <div className="aps-page-narrow">
                    <div className="aps-page-head">
                        <div className="aps-page-head-text">
                            <h1 className="aps-h1">3D App Studio</h1>
                            <p className="aps-page-sub">
                                Every app is born running — bootstrap generates the
                                identity source, auth workflows and predefined journeys.
                            </p>
                        </div>
                        <div className="aps-page-head-actions">
                            <button className="aps-icon-btn" title="Refresh" onClick={load}>
                                <RefreshCw size={15} className={loading ? 'aps-spin' : undefined} />
                            </button>
                            <button
                                className="aps-btn aps-btn-secondary aps-btn-sm"
                                title="Interactive guide — everything you can build, with live playgrounds"
                                onClick={() => nav(`/organizations/${orgId}/3dapp-studio/guide`)}
                            >
                                <BookOpen size={14} />
                                Guide
                            </button>
                            <button className="aps-btn aps-btn-primary aps-btn-sm" onClick={() => setCreateOpen(true)}>
                                <Plus size={14} />
                                Create App
                            </button>
                        </div>
                    </div>
                    {listUnavailable && (
                        <div className="aps-card" style={{
                            padding: '14px 18px',
                            marginBottom: 16,
                            borderColor: 'var(--aps-warning)'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--aps-text-sm)',
                                color: 'var(--aps-text-2)'
                            }}>
                                The org-level app list endpoint
                                (<code className="aps-mono">GET /cm/app-engine/apps?org_id=</code>)
                                is not available yet. Create a new app, or open an
                                existing one by id:
                            </p>
                            <div style={{
                                display: 'flex',
                                gap: 8,
                                marginTop: 10,
                                maxWidth: 420
                            }}>
                                <input className="aps-input aps-input-mono" placeholder="app id…"
                                    value={manualId}
                                    onChange={(e) => setManualId(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && manualId.trim()) openApp(manualId.trim());
                                    }} />
                                <button className="aps-btn aps-btn-secondary"
                                    disabled={!manualId.trim()}
                                    onClick={() => openApp(manualId.trim())}>
                                    Open
                                </button>
                            </div>
                        </div>
                    )}
                    {!loading && !listUnavailable && apps.length === 0 && (
                        <div className="aps-empty">
                            <span className="aps-empty-icon"><Boxes size={24} /></span>
                            <p style={{
                                margin: 0,
                                fontWeight: 600,
                                color: 'var(--aps-text-2)'
                            }}>
                                No apps yet
                            </p>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--aps-text-sm)'
                            }}>
                                Create your first app — it will be usable the second
                                the bootstrap finishes.
                            </p>
                            <button className="aps-btn aps-btn-primary" onClick={() => setCreateOpen(true)}>
                                <Plus size={15} />
                                Create App
                            </button>
                        </div>
                    )}
                    <div className="aps-app-grid">
                        {apps.map((a) => (
                            <div key={a.id} className="aps-app-card aps-card-hover"
                                role="button" tabIndex={0}
                                onClick={() => openApp(a.id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') openApp(a.id);
                                }}>
                                {!a.demo && (
                                    <button className="aps-icon-btn aps-app-card-edit"
                                        title="Rename app"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setRenameTarget(a);
                                        }}>
                                        <Pencil size={14} />
                                    </button>
                                )}
                                <button
                                    className={`aps-icon-btn aps-app-card-dup${a.demo ? ' aps-app-card-dup-solo' : ''}`}
                                    title="Duplicate app"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDuplicateTarget(a);
                                    }}>
                                    <Copy size={14} />
                                </button>
                                {!a.demo && (
                                    <button className="aps-icon-btn aps-app-card-del"
                                        title="Delete app"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteTarget(a);
                                        }}>
                                        <Trash2 size={14} />
                                    </button>
                                )}
                                <span className="aps-app-card-icon"><Boxes size={20} /></span>
                                <span style={{
                                    fontWeight: 700,
                                    fontSize: 'var(--aps-text-md)'
                                }}>
                                    {a.name || a.id}
                                </span>
                                <span className="aps-mono" style={{
                                    fontSize: 'var(--aps-text-xs)',
                                    color: 'var(--aps-text-muted)'
                                }}>
                                    {a.id}
                                </span>
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    color: 'var(--aps-primary)',
                                    fontSize: 'var(--aps-text-sm)',
                                    fontWeight: 600
                                }}>
                                    Open studio <ArrowRight size={14} />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <CreateAppDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                orgId={orgId}
                onCreated={(appId) => {
                    setCreateOpen(false);
                    openApp(appId);
                }}
            />
            <ConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
                title="Delete app?"
                description={(
                    <span>
                        This permanently deletes{' '}
                        <strong>{deleteTarget?.name || deleteTarget?.id}</strong>{' '}
                        — its config (sources, workflows, journeys) AND all its
                        data (users, roles, runs). This cannot be undone.
                    </span>
                )}
                confirmLabel="Delete app"
                tone="danger"
                busy={deleting}
                onConfirm={doDelete}
            />
            <NamePromptDialog
                open={!!duplicateTarget}
                onOpenChange={(o) => { if (!o) setDuplicateTarget(null); }}
                title="Duplicate app"
                icon={<Copy size={18} color="var(--aps-primary)" />}
                description={(
                    <span>
                        Copies the full config (sources, workflows, journeys,
                        roles, users) under fresh ids — business data is not
                        copied. Name the copy:
                    </span>
                )}
                label="New app name"
                initialValue={duplicateTarget
                    ? nextDuplicateName(duplicateTarget.name || duplicateTarget.id, apps)
                    : ''}
                confirmLabel="Create duplicate"
                onConfirm={(name) => {
                    if (duplicateTarget) doDuplicate(duplicateTarget, name);
                }}
            />
            <NamePromptDialog
                open={!!renameTarget}
                onOpenChange={(o) => { if (!o) setRenameTarget(null); }}
                title="Rename app"
                icon={<Pencil size={18} color="var(--aps-primary)" />}
                description={(
                    <span>
                        Changes the app's display name only — its id and all
                        config/data stay exactly as they are.
                    </span>
                )}
                label="App name"
                initialValue={renameTarget ? (renameTarget.name || renameTarget.id) : ''}
                confirmLabel="Rename"
                onConfirm={(name) => {
                    if (renameTarget) doRename(renameTarget, name);
                }}
            />
            {busyAction && (
                <div className="aps-busy-overlay" role="status" aria-live="polite">
                    <div className="aps-busy-card aps-card">
                        <Loader2 size={28} className="aps-spin" color="var(--aps-primary)" />
                        <p className="aps-busy-title">
                            {busyAction.kind === 'duplicate'
                                ? <>Creating duplicate <strong>“{busyAction.name}”</strong>…</>
                                : <>Renaming to <strong>“{busyAction.name}”</strong>…</>}
                        </p>
                        <p className="aps-busy-sub">
                            This can take a few seconds — please wait.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
